import { type FC, useState } from 'react';

import { Permission } from '@/api/permissions/models/Permission';
import { usePortfoliosQuery } from '@/api/portfolios';
import { Column, Row } from '@/components/Grid';
import { useHasPermission } from '@/components/Permissions/useHasPermission';
import { PortfolioGroupSelect } from '@/components/PortfolioGroupSelect';
import { AssetAllocationTableWidget } from '@/components/Widgets/AssetAllocation/AssetAllocationTable/AssetAllocationTableWidget';
import { PortfolioPerformanceWidget } from '@/components/Widgets/Portfolios/PortfolioPerformanceWidget';
import { LatestMonthlyReportsWidget } from '@/components/Widgets/Reports/LatestMonthlyReports/LatestMonthlyReportsWidget';
import { WithGap } from '@/components/WithGap';
import { OngoingOrdersWidget } from '@/pages/OrderList/OngoingOrdersWidget';

import { EsgOverviewWidget } from './EsgOverviewWidget';
import { NewsArticlesWidget } from './NewsArticlesWidget';
import { TradingLinks } from './TradingLinks';

interface PortfolioDashboardProps {
  preferredPortfolioGroupId: string;
}

export const PortfolioDashboard: FC<PortfolioDashboardProps> = ({
  preferredPortfolioGroupId,
}) => {
  const hasPermission = useHasPermission();

  const query = usePortfoliosQuery();

  const [selectedPortfolioGroupId, setActivePortfolioGroupId] =
    useState<string>(preferredPortfolioGroupId);

  const portfolioIds = query.data!.portfolios.map((portfolio) => portfolio.id);

  const handleChange = (portfolioGroupId: string): void => {
    setActivePortfolioGroupId(portfolioGroupId);
  };

  return (
    <>
      {query.data!.portfolioGroups.length > 1 && (
        <Row>
          <Column $span={5}>
            <PortfolioGroupSelect
              onChange={handleChange}
              selectedPortfolioGroupId={selectedPortfolioGroupId}
            />
          </Column>
        </Row>
      )}
      <WithGap gap="96">
        <PortfolioPerformanceWidget
          portfolioGroup={selectedPortfolioGroupId}
          showMoreLink
        />

        {hasPermission(Permission.OrdersRead) && <TradingLinks />}

        <AssetAllocationTableWidget
          portfolioGroupShortName={selectedPortfolioGroupId}
        />

        <EsgOverviewWidget portfolioGroupId={selectedPortfolioGroupId} />

        {hasPermission(Permission.ReportsRead) && (
          <LatestMonthlyReportsWidget />
        )}

        {hasPermission(Permission.OrdersRead) && (
          <OngoingOrdersWidget portfolioIds={portfolioIds} />
        )}

        <NewsArticlesWidget />
      </WithGap>
    </>
  );
};
